/**
 * Platform Detection Utility
 * 
 * Detects whether the app is running in a native iOS/Android app (via Natively/BuildNatively)
 * or in a regular web browser.
 * 
 * This is used to conditionally load native SDKs (HealthKit, Notifications) only when needed.
 */

// Extend Window interface with Natively SDK types
// Note: The core Natively SDK (from natively.min.js) uses lowercase 'window.natively'
// The specialized SDKs (NativelyHealth, NativelyNotifications) use uppercase
declare global {
  interface Window {
    NativelyInfo?: any;
    // NativelyHealth is defined in natively-notifications.d.ts with proper typing
    // Core Natively SDK - JavaScript SDK uses lowercase 'natively'
    natively?: {
      openExternalURL?: (url: string, external: boolean) => void;
      openExternalApp?: (url: string) => void;
      setAppBackgroundColor?: (color: string) => void;
      setAppStatusBarStyleIOS?: (style: 'DARK' | 'LIGHT' | 'NONE') => void;
    };
    NativelyAudioRecorder?: any;
  }
}

// Cache the result of async platform detection
let _isNativeAppCache: boolean | null = null;
let _platformInfoCache: any = null;

/**
 * Check if running in a native app webview (Natively/BuildNatively) - ASYNC VERSION
 * 
 * This is the most reliable method as it actually tries to fetch app info from the SDK.
 * If the SDK can return valid app info, we know we're in the native app.
 */
export const detectNativeApp = async (): Promise<{
  isNative: boolean;
  platformInfo?: {
    osName?: string;
    osVersion?: string;
    device?: string;
    buildVersion?: string;
    buildNumber?: string;
    sdkVersion?: number;
  };
}> => {
  if (typeof window === 'undefined') {
    return { isNative: false }; // Server-side rendering
  }

  // Return cached result if available
  if (_isNativeAppCache !== null) {
    return { 
      isNative: _isNativeAppCache, 
      platformInfo: _platformInfoCache 
    };
  }

  // Try to use NativelyInfo SDK to get app info
  // This is the most reliable method
  if (window.NativelyInfo) {
    try {
      const info = new window.NativelyInfo();
      
      // Create a promise to wait for the callback
      const appInfo = await new Promise<any>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for app info'));
        }, 2000);

        info.getAppInfo((resp: any) => {
          clearTimeout(timeout);
          resolve(resp);
        });
      });

      // If we got valid app info, we're definitely in the native app
      if (appInfo && (appInfo.osName === 'iOS' || appInfo.osName === 'Android')) {
        _isNativeAppCache = true;
        _platformInfoCache = {
          osName: appInfo.osName,
          osVersion: appInfo.osVersion,
          device: appInfo.device,
          buildVersion: appInfo.buildVersion,
          buildNumber: appInfo.buildNumber,
          sdkVersion: appInfo.sdkVersion,
        };
        console.log('[Platform] Native app detected via NativelyInfo:', _platformInfoCache);
        return { isNative: true, platformInfo: _platformInfoCache };
      }
    } catch (error) {
      console.log('[Platform] Failed to get app info from NativelyInfo:', error);
      // Fall through to other detection methods
    }
  }

  // Fallback: Check for other Natively SDK objects
  const hasNativelySDK = !!(
    window.NativelyHealth ||
    window.NativelyNotifications ||
    window.NativelyPurchases ||
    window.natively
  );

  if (hasNativelySDK) {
    _isNativeAppCache = true;
    console.log('[Platform] Native app detected via SDK presence');
    return { isNative: true };
  }

  // Enhanced WebView detection for iOS
  const isIOSWebView = () => {
    // Check user agent for native app markers first (most reliable)
    const ua = navigator.userAgent.toLowerCase();
    const nativeMarkers = ['natively', 'buildnatively', 'wkwebview'];
    if (nativeMarkers.some(marker => ua.includes(marker))) {
      return true;
    }
    
    // Check for webkit message handlers (reliable indicator of iOS WebView)
    // BUT: Only if we're not in a known browser
    // Chrome, Firefox, etc. on iOS also have webkit, but they're real browsers
    if ((window as any).webkit && (window as any).webkit.messageHandlers) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      // Known iOS browsers have specific markers in their user agent
      const isKnownBrowser = /Safari|CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
      
      // If we have webkit handlers on iOS but NO browser markers, it's a WebView
      if (isIOS && !isKnownBrowser) {
        return true;
      }
    }
    
    return false;
  };

  // Check for iOS WebView indicators
  if (isIOSWebView()) {
    _isNativeAppCache = true;
    console.log('[Platform] Native app detected via iOS WebView indicators');
    return { isNative: true };
  }

  // If all detection methods fail, assume web browser
  _isNativeAppCache = false;
  // Reduce console noise in web browsers - only log in dev mode
  console.log('[Platform] Web browser detected');
  return { isNative: false };
};

/**
 * Check if running in a native app webview (Natively/BuildNatively) - SYNC VERSION
 * 
 * This is a synchronous version that uses cached results or quick checks.
 * For initial detection, use detectNativeApp() instead.
 */
export const isNativeApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false; // Server-side rendering
  }

  // Return cached result if available
  if (_isNativeAppCache !== null) {
    return _isNativeAppCache;
  }

  // Enhanced WebView detection for iOS
  const isIOSWebView = () => {
    // Check user agent for native app markers first (most reliable)
    const ua = navigator.userAgent.toLowerCase();
    const nativeMarkers = ['natively', 'buildnatively', 'wkwebview'];
    if (nativeMarkers.some(marker => ua.includes(marker))) {
      return true;
    }
    
    // Check for webkit message handlers (reliable indicator of iOS WebView)
    // BUT: Only if we're not in a known browser
    // Chrome, Firefox, etc. on iOS also have webkit, but they're real browsers
    if ((window as any).webkit && (window as any).webkit.messageHandlers) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      // Known iOS browsers have specific markers in their user agent
      const isKnownBrowser = /Safari|CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
      
      // If we have webkit handlers on iOS but NO browser markers, it's a WebView
      if (isIOS && !isKnownBrowser) {
        return true;
      }
    }
    
    return false;
  };

  // Quick check for SDK objects
  const hasNativelySDK = !!(
    window.NativelyInfo ||
    window.NativelyHealth ||
    window.NativelyNotifications ||
    window.NativelyPurchases ||
    window.natively
  );
  
  // Check for iOS WebView indicators
  const isWebView = isIOSWebView();

  return hasNativelySDK || isWebView;
};

/**
 * Check if running in a web browser
 */
export const isWebBrowser = (): boolean => {
  return !isNativeApp();
};

/**
 * Get platform type as string
 */
export const getPlatform = (): 'native' | 'web' => {
  return isNativeApp() ? 'native' : 'web';
};

/**
 * Check if HealthKit is available (iOS native app only)
 */
export const isHealthKitAvailable = (): boolean => {
  return isNativeApp() && typeof window !== 'undefined' && !!window.NativelyHealth;
};

/**
 * Check if push notifications are available (native app only)
 */
export const isNotificationsAvailable = (): boolean => {
  return isNativeApp() && typeof window !== 'undefined' && !!window.NativelyNotifications;
};

/**
 * Check if in-app purchases are available (native app only)
 */
export const isPurchasesAvailable = (): boolean => {
  return isNativeApp() && typeof window !== 'undefined' && !!window.NativelyPurchases;
};

/**
 * Check if Apple Sign In is available (iOS native app only)
 */
export const isAppleSignInAvailable = (): boolean => {
  return isNativeApp() && isIOS() && typeof window !== 'undefined' && !!window.NativelyAppleSignInService;
};

/**
 * Wait for the NativelyHealth SDK to load (for use in native apps)
 * Returns true if SDK loads within timeout, false otherwise
 */
export const waitForHealthKitSDK = async (
  maxWaitMs: number = 5000,
  checkIntervalMs: number = 500
): Promise<boolean> => {
  // First, run the async detection to properly detect native app
  const detection = await detectNativeApp();
  
  if (!detection.isNative) {
    console.log('[Platform] Not in native app, skipping HealthKit SDK wait');
    return false;
  }

  console.log('[Platform] Native app detected, waiting for HealthKit SDK...');
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (typeof window !== 'undefined' && window.NativelyHealth) {
      console.log('[Platform] NativelyHealth SDK loaded');
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
  }
  
  console.log('[Platform] Timeout waiting for NativelyHealth SDK');
  return false;
};

/**
 * Wait for the NativelyNotifications SDK to load (for use in native apps)
 * Returns true if SDK loads within timeout, false otherwise
 */
export const waitForNotificationsSDK = async (
  maxWaitMs: number = 5000,
  checkIntervalMs: number = 500
): Promise<boolean> => {
  // First, run the async detection to properly detect native app
  const detection = await detectNativeApp();
  
  if (!detection.isNative) {
    console.log('[Platform] Not in native app, skipping Notifications SDK wait');
    return false;
  }

  console.log('[Platform] Native app detected, waiting for Notifications SDK...');
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (typeof window !== 'undefined' && window.NativelyNotifications) {
      console.log('[Platform] NativelyNotifications SDK loaded');
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
  }
  
  console.log('[Platform] Timeout waiting for NativelyNotifications SDK');
  return false;
};

/**
 * Wait for the NativelyAppleSignInService SDK to load (for use in iOS native apps)
 * Returns true if SDK loads within timeout, false otherwise
 */
export const waitForAppleSignInSDK = async (
  maxWaitMs: number = 5000,
  checkIntervalMs: number = 500
): Promise<boolean> => {
  // First, run the async detection to properly detect native app
  const detection = await detectNativeApp();
  
  if (!detection.isNative || !isIOS()) {
    console.log('[Platform] Not in iOS native app, skipping Apple Sign In SDK wait');
    return false;
  }

  console.log('[Platform] iOS native app detected, waiting for Apple Sign In SDK...');
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (typeof window !== 'undefined' && window.NativelyAppleSignInService) {
      console.log('[Platform] NativelyAppleSignInService SDK loaded');
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
  }
  
  console.log('[Platform] Timeout waiting for NativelyAppleSignInService SDK');
  return false;
};

/**
 * Check if running on iOS (native or web)
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const platformInfo = _platformInfoCache;
  if (platformInfo?.osName === 'iOS') return true;
  
  // Fallback: check user agent
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
};

/**
 * Check if running on Android (native or web)
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const platformInfo = _platformInfoCache;
  if (platformInfo?.osName === 'Android') return true;
  
  // Fallback: check user agent
  return /Android/i.test(window.navigator.userAgent);
};

/**
 * Open a URL in an external browser (Safari on iOS)
 * This is essential for OAuth flows in native iOS apps to avoid WebView restrictions
 * 
 * In native iOS apps, OAuth providers often block authentication in embedded WebViews
 * for security reasons. This function opens the URL in the system browser (Safari).
 * 
 * @param url - The URL to open
 * @returns Promise<{success: boolean, error?: string}> - success status and optional error message
 */
export const openInExternalBrowser = async (url: string): Promise<{ success: boolean; error?: string }> => {
  if (typeof window === 'undefined') {
    const error = 'Cannot open URL in server-side context';
    console.warn('[Platform]', error);
    return { success: false, error };
  }

  const detection = await detectNativeApp();
  
  if (detection.isNative) {
    console.log('[Platform] Opening URL in external browser (native app):', url);
    
    // Use the Natively SDK (lowercase 'natively')
    if (window.natively?.openExternalURL) {
      try {
        // Use external=true to open in system browser (Safari/Chrome)
        // This is required for Google OAuth (in-app browsers are blocked)
        window.natively.openExternalURL(url, true);
        console.log('[Platform] ✓ Called natively.openExternalURL with external=true');
        return { success: true };
      } catch (error) {
        const errorMsg = 'Failed to open browser. Please try again.';
        console.error('[Platform] ✗ natively.openExternalURL failed:', error);
        return { success: false, error: errorMsg };
      }
    }
    
    // No API available
    const error = 'Could not automatically open Safari. The native browser API is not available.';
    console.warn('[Platform] window.natively.openExternalURL not available');
    console.warn('[Platform] Available APIs:', {
      'window.natively': !!window.natively,
      'window.natively.openExternalURL': !!window.natively?.openExternalURL,
    });
    return { success: false, error };
  }
  
  // Not in native app - just open in new tab
  console.log('[Platform] Opening URL in new tab (web browser)');
  window.open(url, '_blank');
  return { success: true };
};

/**
 * Check if the app supports opening external browsers
 */
export const supportsExternalBrowser = async (): Promise<boolean> => {
  const detection = await detectNativeApp();
  return detection.isNative;
};

// Flag to prevent multiple initializations
let _navigationInterceptorInitialized = false;

/**
 * Initialize navigation interceptor for native apps
 * This intercepts all navigations and redirects to use external browser
 * 
 * Call this early in your app initialization to ensure all navigations
 * are properly routed through the native external browser handler.
 */
export const initializeNavigationInterceptor = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  
  // Prevent multiple initializations
  if (_navigationInterceptorInitialized) {
    console.log('[Platform] Navigation interceptor already initialized');
    return;
  }
  
  const detection = await detectNativeApp();
  if (!detection.isNative) {
    console.log('[Platform] Skipping navigation interceptor - not in native app');
    return;
  }
  
  console.log('[Platform] Initializing navigation interceptor for native app...');
  
  // Check if Natively SDK is available (lowercase 'natively')
  const hasNativelyAPI = !!window.natively?.openExternalURL;
  
  if (!hasNativelyAPI) {
    console.warn('[Platform] window.natively.openExternalURL not available - interceptor may not work');
    console.warn('[Platform] Available APIs:', {
      'window.natively': !!window.natively,
    });
  } else {
    console.log('[Platform] Natively API available:', {
      'natively.openExternalURL': !!window.natively?.openExternalURL,
    });
  }
  
  // Helper to open URL using available API
  const openExternal = (url: string) => {
    if (window.natively?.openExternalURL) {
      window.natively.openExternalURL(url, true);
    }
  };
  
  // 1. Intercept window.open calls
  const originalOpen = window.open.bind(window);
  window.open = function(url?: string | URL, target?: string, features?: string): Window | null {
    if (url && hasNativelyAPI) {
      const urlString = url.toString();
      console.log('[Platform] Intercepted window.open:', urlString);
      openExternal(urlString);
      return null; // Indicate we handled it
    }
    return originalOpen(url, target, features);
  };
  
  // 2. Intercept link clicks
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    
    if (anchor?.href && hasNativelyAPI) {
      // Check if it's an external link (not same-origin navigation)
      const currentOrigin = window.location.origin;
      const linkUrl = new URL(anchor.href, window.location.href);
      
      // If link is to a different origin, intercept it
      if (linkUrl.origin !== currentOrigin) {
        console.log('[Platform] Intercepted external link click:', anchor.href);
        event.preventDefault();
        event.stopPropagation();
        openExternal(anchor.href);
      }
    }
  }, true); // Use capture phase
  
  // 3. Intercept form submissions that go to external URLs
  document.addEventListener('submit', (event) => {
    const form = event.target as HTMLFormElement;
    const action = form.action;
    
    if (action && hasNativelyAPI) {
      const currentOrigin = window.location.origin;
      const actionUrl = new URL(action, window.location.href);
      
      // If form action is to a different origin, intercept it
      if (actionUrl.origin !== currentOrigin) {
        console.log('[Platform] Intercepted form submission to external URL:', action);
        event.preventDefault();
        openExternal(action);
      }
    }
  }, true);
  
  // 4. Monitor for programmatic location changes
  // Only attempt this if the property is configurable
  try {
    const originalLocationDescriptor = Object.getOwnPropertyDescriptor(window, 'location');
    
    // Check if we can safely redefine the property
    if (originalLocationDescriptor && originalLocationDescriptor.configurable !== false) {
      let isIntercepting = false; // Prevent infinite loops
      
      Object.defineProperty(window, 'location', {
        get: () => originalLocationDescriptor.get!.call(window),
        set: (value) => {
          if (isIntercepting) {
            originalLocationDescriptor.set!.call(window, value);
            return;
          }
          
          const newUrl = value.toString();
          const currentOrigin = window.location.origin;
          
          try {
            const targetUrl = new URL(newUrl, window.location.href);
            
            // If navigating to external origin, intercept
            if (targetUrl.origin !== currentOrigin && hasNativelyAPI) {
              console.log('[Platform] Intercepted location change to external URL:', newUrl);
              openExternal(newUrl);
              return;
            }
          } catch (error) {
            // Invalid URL, let it through
          }
          
          // Allow same-origin navigation
          isIntercepting = true;
          originalLocationDescriptor.set!.call(window, value);
          isIntercepting = false;
        },
        configurable: true // Make it reconfigurable if needed
      });
      console.log('[Platform] ✓ Location interceptor installed');
    } else {
      console.log('[Platform] ⚠ window.location is not configurable, skipping location interceptor');
    }
  } catch (error) {
    console.warn('[Platform] Could not install location interceptor:', error);
    // Continue without location interception - other methods will still work
  }
  
  _navigationInterceptorInitialized = true;
  console.log('[Platform] ✓ Navigation interceptor initialized');
  console.log('[Platform] All external navigations will use native external browser API');
};

/**
 * Get the app version string
 * - For native apps: Returns the buildVersion (e.g., "1.2.3") from NativelyInfo
 * - For web: Returns the version from package.json
 * 
 * @returns Promise<string> - The version string (e.g., "1.0.1" or "1.2.3 native")
 */
export const getAppVersion = async (): Promise<string> => {
  const WEB_VERSION = '1.0.3';
  
  const detection = await detectNativeApp();
  
  if (detection.isNative && detection.platformInfo?.buildVersion) {
    // Native app with version info available
    return `${detection.platformInfo.buildVersion} native`;
  } else if (detection.isNative) {
    // Native app but version info not available
    return `${WEB_VERSION} native`;
  } else {
    // Web browser
    return WEB_VERSION;
  }
};

/**
 * Log platform detection info (for debugging)
 * This now uses the async detection method for accurate results
 */
export const logPlatformInfo = async (): Promise<void> => {
  const detection = await detectNativeApp();
  
  const info = {
    platform: detection.isNative ? 'native' : 'web',
    isNative: detection.isNative,
    isWeb: !detection.isNative,
    isIOS: isIOS(),
    platformInfo: detection.platformInfo,
    healthKitAvailable: isHealthKitAvailable(),
    notificationsAvailable: isNotificationsAvailable(),
    appleSignInAvailable: isAppleSignInAvailable(),
    supportsExternalBrowser: await supportsExternalBrowser(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'N/A',
    // Additional debug info
    hasNativelyInfo: typeof window !== 'undefined' && !!window.NativelyInfo,
    hasNativelyHealth: typeof window !== 'undefined' && !!window.NativelyHealth,
    hasNativelyNotifications: typeof window !== 'undefined' && !!window.NativelyNotifications,
    hasNativelyAppleSignIn: typeof window !== 'undefined' && !!window.NativelyAppleSignInService,
    // Check the core Natively SDK (lowercase)
    hasNativelyOpenURL: typeof window !== 'undefined' && !!window.natively?.openExternalURL,
  };
  
  console.log('[Platform Detection]', info);
  
  // Log a warning if we detect native markers but SDK isn't loaded
  if (info.isNative && !info.hasNativelyHealth && !info.hasNativelyNotifications) {
    console.warn('[Platform Detection] Native app detected but Natively SDKs not found. They may load later.');
  }
};
